import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import localStore from 'store2';

import { IProfile, planFeatureFlags } from 'models/profileModel';
import { getPreferredLanguage, getUserData } from 'services/user/userApi';
import { getAccountCredit } from 'services/accountCredit/accountCreditApi';
import { getAccountPlanFeatureValue } from 'services/subscriptionManagement/subscriptionManagementApi';
import { SetUpPosthog } from 'components/features/shared/SetUpPosthog';

export const fetchUser = createAsyncThunk('user/fetchUser', async () => {
  const currentUser = localStore.get(process.env.REACT_APP_LOCAL_STORAGE_CURRENT_USER) as IProfile
  if (currentUser !== null && currentUser !== undefined) {
    return currentUser
  }
  const userData = await getUserData();
  localStore.set(process.env.REACT_APP_LOCAL_STORAGE_CURRENT_USER, userData);
  SetUpPosthog();
  return userData;
});

export const fetchAccountCredit = createAsyncThunk('account/fetchCredit', async () => {
  const credit = await getAccountCredit();
  return credit;
});

export const fetchPreferredLanguage = createAsyncThunk('user/fetchPreferredLanguage', async () => {
  const UILanguage = await getPreferredLanguage();
  return UILanguage;
});

// TODO: Pass feature code as argument when entitlement is ready
export const fetchPlanFeatureValue = createAsyncThunk('user/fetchPlanFeatureValue', async () => {
  const chatbotFeature = await getAccountPlanFeatureValue(planFeatureFlags.isChatbotEnabled);
  const conversationalCommerceFeature = await getAccountPlanFeatureValue(planFeatureFlags.isConversationalCommerceEnabled);
  const aiVoiceAgentFeature = await getAccountPlanFeatureValue(planFeatureFlags.isAiVoiceAgentEnabled);
  return { chatbot: chatbotFeature, conversationalCommerce: conversationalCommerceFeature, aiVoiceAgent: aiVoiceAgentFeature };
});

interface UserState {
  data: IProfile | null;
  currentCredit: number;
  UIPreferredLanguage: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: UserState = {
  data: null,
  currentCredit: 0.0000,
  UIPreferredLanguage: null,
  loading: false,
  error: null,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    updateUserData: (state, action: PayloadAction<IProfile>) => {
      state.data = { ...action.payload }
      localStore.set(process.env.REACT_APP_LOCAL_STORAGE_CURRENT_USER, action.payload)
    },
    updateWebhookVerification: (state, action: PayloadAction<boolean>) => {
      if (state.data) {
        state.data.isWAWebhookVerified = action.payload
        localStore.set(process.env.REACT_APP_LOCAL_STORAGE_CURRENT_USER, state.data)
      }
    },
    updateUIPreferredLanguage: (state, action: PayloadAction<string>) => {
      state.UIPreferredLanguage = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        localStore.set(process.env.REACT_APP_LOCAL_STORAGE_CURRENT_USER, action.payload)
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'An error occurred.';
      })
      .addCase(fetchAccountCredit.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCredit = action.payload;
      })
      .addCase(fetchPreferredLanguage.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPreferredLanguage.fulfilled, (state, action) => {
        state.loading = false;
        state.UIPreferredLanguage = action.payload ?? null;
      })
      .addCase(fetchPlanFeatureValue.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPlanFeatureValue.fulfilled, (state, action) => {
        state.loading = false;
        if (state.data) {
          state.data.isChatbotEnabled = Boolean(action.payload.chatbot);
          state.data.isConversationalCommerceEnabled = Boolean(action.payload.conversationalCommerce);
          state.data.isAiVoiceAgentEnabled = Boolean(action.payload.aiVoiceAgent);
          localStore.set(process.env.REACT_APP_LOCAL_STORAGE_CURRENT_USER, state.data);
        }
      })
      .addCase(fetchPlanFeatureValue.rejected, (state) => {
        state.loading = false;
        if (state.data) {
          state.data.isChatbotEnabled = false;
          state.data.isConversationalCommerceEnabled = false;
          state.data.isAiVoiceAgentEnabled = false;
          localStore.set(process.env.REACT_APP_LOCAL_STORAGE_CURRENT_USER, state.data);
        }
      });
  },
});

export const { updateUserData, updateWebhookVerification, updateUIPreferredLanguage, setLoading } = userSlice.actions;

export default userSlice;
